import React, { StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { Integrations as TracingIntegrations } from '@sentry/tracing' // Must import after @sentry/react
import { appPath, sentryDSN } from '@fs/zion-config'
import { getSentryConfig } from '@fs/tree-config'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router, useLocation } from '@fs/zion-router'
import { NoticeLoading } from '@fs/zion-icon'
import { GroupsProvider } from '@fs/zion-tree-groups'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import AppLoading from './components/AppLoading'

const EmptyLoading = () => <div />

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to zion's mergeSentryConfig via babylon's common tree-config

  // Docs: https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/
  Sentry.init(
    getSentryConfig({
      integrations: [
        new TracingIntegrations.BrowserTracing({
          // routingInstrumentation: Sentry.reactRouterV5Instrumentation(history), // TODO: Determine what this is for and how to fully integrate it, if we want it
        }),
      ],
      // If we really need specific entries for ignoreErrors, allowUrls, or denyUrls, consider having them added to the tree-config to be applied to all Tree apps, or to zion-config to benefit the entire org.
      ignoreErrors: [],
      allowUrls: [],
      denyUrls: [],
      tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACING_RATE) || 0.05, // .05 = 5% of errors sent (please note that all errors will be up to 20x more severe than the Sentry dashboard will indicate)
    })
  )
}

const RootWrapper = ({ children }) => {
  const { pathname } = useLocation()
  const rootProps =
    pathname?.startsWith('/private-people') || pathname?.startsWith('/recents')
      ? { footer: { useFatFooter: true } }
      : {
          footer: { hidden: true },
          fullScreen: !pathname?.startsWith('/pedigree/descendancy'),
          helperModeProps: { redirectAfterStopUrl: '/tree/pedigree' },
        }

  return (
    // This app has <Subnav> inside <main>. The skipLinkId which matches the id in App.js tells screen readers to skip the subnav and go to the main content
    <Root skipLinkId="skipToMainContent" analytics {...rootProps}>
      {children}
    </Root>
  )
}

const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  /* https://github.com/fs-webdev/zion/pull/10968 broke us hard.
    It removed most react-router path matching logic, due to it passing route strings we provide here through a URL constructor
    That ends up stripping a lot of the regex-based things you can do with path matching (pipes, etc)
    We want to route most things internally here, just not ?treeId=ABC

    We used to have this:
    handledExternalRoutes={[
        { path: `/tree/pedigree/:pid(${pidRegexStr})`, exact: true },
        '/tree/recents',
        '/tree/pedigree/:view(landscape|portrait|first-ancestor|sibling|fanchart)',
      ]}

      But now most of that is broken (can't use pidRegexStr, or pipes), so we're having to be a bit more basic and hacky here.
    */

  return (
    <Router basename={base} handledExternalRoutes={['/tree/pedigree/:pidOrView([a-zA-Z0-9-]+)', '/tree/recents']}>
      <RootWrapper>
        <GroupsProvider LoadingComponent={AppLoading}>
          <App />
        </GroupsProvider>
      </RootWrapper>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} LoadingFallback={EmptyLoading} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
